import React, { useMemo, useState } from 'react';
import { CSVLink } from 'react-csv';
import {
  Box,
  Button,
  Chip,
  FormControlLabel,
  LinearProgress,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import { formatScore, formatValue } from './formatters';
import {
  allMetricColumns,
  enrollmentColumnLabel,
  keyMetricColumns,
  qualityAverageValue,
  qualityMetricNote,
} from './indicatorConfig';
import { TOTAL_SCORE_MAX, toPoints } from './scoreScale';

// Column header for the entity-name column. Keeps the existing Connecticut
// district/region labels (`School District` / `Region` / `Planning Region`)
// as-is, since those have a special "collapsed" display name; any other
// level (e.g. Arkansas's county tier) falls back to its own entityNoun,
// e.g. "County".
function nameColumnLabel({ level, levelConfig, showAllMetrics }) {
  if (level === 'district') return 'School District';
  if (level === 'region') return showAllMetrics ? 'Region' : 'Planning Region';
  if (levelConfig?.entityNoun) return levelConfig.entityNoun.replace(/^./, (c) => c.toUpperCase());
  return 'Name';
}

const METRIC_SORT_PREFIX = 'value:';
const NUMERIC_CELL_SX = {
  fontVariantNumeric: 'tabular-nums',
  whiteSpace: 'nowrap',
};
const HEADER_CELL_SX = {
  bgcolor: 'grey.50',
  fontWeight: 600,
  verticalAlign: 'top',
  whiteSpace: 'normal',
};
const headerSortLabelSx = (align) => ({
  whiteSpace: 'normal',
  alignItems: 'flex-start',
  textAlign: align,
  lineHeight: 1.25,
  ...(align === 'right' ? { justifyContent: 'flex-end', ml: 'auto' } : null),
  '& .MuiTableSortLabel-icon': { flexShrink: 0, mt: '1px' },
});

const metricSortKey = (id) => `${METRIC_SORT_PREFIX}${id}`;
const isMetricSortKey = (key) => String(key).startsWith(METRIC_SORT_PREFIX);
const metricIdFromSortKey = (key) => String(key).slice(METRIC_SORT_PREFIX.length);

const contributionFor = (row, id) => row.contributions.find((item) => item.id === id);

const numericContributionValue = (row, id) => {
  const contribution = contributionFor(row, id);
  if (!contribution || contribution.missing) return null;
  const numeric = Number(contribution.value);
  return Number.isFinite(numeric) ? numeric : null;
};

const metricNumericValue = (row, column, indicators) => {
  if (column.composite === 'qualityAverage') {
    return qualityAverageValue(row.entity, indicators);
  }
  return numericContributionValue(row, column.id);
};

const metricDisplayValue = (row, column, indicators) => {
  if (column.composite === 'qualityAverage') {
    return formatValue(qualityAverageValue(row.entity, indicators), column.format);
  }
  const contribution = contributionFor(row, column.id);
  return formatValue(contribution?.value, column.format);
};

const csvCell = (value) => (value === null || value === undefined ? '' : value);

export function buildRankingCsv({ rows, indicators, level, levelConfig }) {
  const metaLabel = levelConfig?.metaLabel;
  const metaField = levelConfig?.metaField;
  const hasAlliance = Boolean(levelConfig?.hasAlliance);
  const headers = [
    { label: 'Rank', key: 'rank' },
    { label: 'Name', key: 'name' },
    ...(metaLabel ? [{ label: metaLabel, key: 'meta' }] : []),
    ...(hasAlliance ? [{ label: 'Alliance', key: 'alliance' }] : []),
    { label: 'Opportunity Score', key: 'total' },
    { label: 'Enrollment', key: 'enrollment' },
    { label: 'Charter Share', key: 'charter' },
    { label: 'Indicators Scored', key: 'coverage' },
    ...indicators.flatMap((indicator) => [
      { label: `${indicator.label} value`, key: `${indicator.id}_value` },
      { label: `${indicator.label} score`, key: `${indicator.id}_score` },
      { label: `${indicator.label} contribution`, key: `${indicator.id}_weighted` },
    ]),
  ];

  const data = rows.map((row) => {
    const record = {
      rank: row.rank,
      name: row.entity.name,
      total: toPoints(row.total)?.toFixed(4),
      enrollment: row.entity.k12Enrollment2025_2026,
      charter: row.entity.pctPublicStudentsCharter,
      coverage: `${row.coverage.scored}/${row.coverage.total}`,
    };
    if (metaLabel) record.meta = metaField ? row.entity[metaField] : '';
    if (hasAlliance) record.alliance = row.entity.allianceDistrict ? 'Yes' : '';
    row.contributions.forEach((item) => {
      record[`${item.id}_value`] = item.missing ? '' : csvCell(item.value);
      record[`${item.id}_score`] =
        item.missing || item.score == null ? '' : toPoints(item.score);
      record[`${item.id}_weighted`] = toPoints(item.weighted);
    });
    return record;
  });

  return { headers, data };
}

const RankingTable = ({
  rows,
  indicators,
  level,
  levelConfig,
  csvPrefix = 'city-explorer',
  search,
  onSearch,
  showAllMetrics,
  onToggleAllMetrics,
  onSelect,
  selectedName,
}) => {
  const metaField = levelConfig?.metaField;
  const hasAlliance = Boolean(levelConfig?.hasAlliance);
  const [sort, setSort] = useState({ key: 'rank', direction: 'asc' });
  const metricColumns = useMemo(() => keyMetricColumns(indicators), [indicators]);
  const allMetricsColumns = useMemo(() => allMetricColumns(indicators), [indicators]);
  const extraColumns = showAllMetrics ? allMetricsColumns : metricColumns;

  const sortedRows = useMemo(() => {
    const copy = [...rows];
    copy.sort((a, b) => {
      const dir = sort.direction === 'asc' ? 1 : -1;
      if (sort.key === 'rank' || sort.key === 'total') {
        const av = sort.key === 'rank' ? a.rank : a.total;
        const bv = sort.key === 'rank' ? b.rank : b.total;
        return (av - bv) * dir;
      }
      if (sort.key === 'name') {
        return String(a.entity.name).localeCompare(String(b.entity.name)) * dir;
      }
      if (sort.key === 'enrollment') {
        const av = Number(a.entity.k12Enrollment2025_2026);
        const bv = Number(b.entity.k12Enrollment2025_2026);
        const aMissing = !Number.isFinite(av);
        const bMissing = !Number.isFinite(bv);
        if (aMissing && bMissing) return String(a.entity.name).localeCompare(String(b.entity.name));
        if (aMissing) return 1;
        if (bMissing) return -1;
        if (av !== bv) return (av - bv) * dir;
        return String(a.entity.name).localeCompare(String(b.entity.name));
      }
      if (isMetricSortKey(sort.key)) {
        const id = metricIdFromSortKey(sort.key);
        const column = extraColumns.find((item) => item.id === id) || { id };
        const av = metricNumericValue(a, column, indicators);
        const bv = metricNumericValue(b, column, indicators);
        if (av === null && bv === null) return String(a.entity.name).localeCompare(String(b.entity.name));
        if (av === null) return 1;
        if (bv === null) return -1;
        if (av !== bv) return (av - bv) * dir;
        return String(a.entity.name).localeCompare(String(b.entity.name));
      }
      const contributionA = contributionFor(a, sort.key);
      const contributionB = contributionFor(b, sort.key);
      const av = contributionA?.missing || contributionA?.score == null ? null : contributionA.score;
      const bv = contributionB?.missing || contributionB?.score == null ? null : contributionB.score;
      if (av === null && bv === null) return String(a.entity.name).localeCompare(String(b.entity.name));
      if (av === null) return 1;
      if (bv === null) return -1;
      if (av !== bv) return (av - bv) * dir;
      return String(a.entity.name).localeCompare(String(b.entity.name));
    });
    return copy;
  }, [rows, sort, extraColumns, indicators]);

  const visibleRows = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return sortedRows;
    return sortedRows.filter((row) => {
      const haystack = [
        row.entity.name,
        metaField ? row.entity[metaField] : '',
        hasAlliance && row.entity.allianceDistrict ? 'alliance' : '',
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(query);
    });
  }, [sortedRows, search, metaField, hasAlliance]);

  const { headers: csvHeaders, data: csvData } = useMemo(
    () => buildRankingCsv({ rows, indicators, level, levelConfig }),
    [rows, indicators, level, levelConfig]
  );

  const handleSort = (key) => {
    setSort((current) =>
      current.key === key
        ? { key, direction: current.direction === 'asc' ? 'desc' : 'asc' }
        : { key, direction: key === 'name' ? 'asc' : 'desc' }
    );
  };

  const headerCell = (key, label, align = 'left') => (
    <TableCell
      align={align}
      sortDirection={sort.key === key ? sort.direction : false}
      sx={HEADER_CELL_SX}
    >
      <TableSortLabel
        active={sort.key === key}
        direction={sort.key === key ? sort.direction : 'asc'}
        onClick={() => handleSort(key)}
        sx={headerSortLabelSx(align)}
      >
        {label}
      </TableSortLabel>
    </TableCell>
  );

  const metricHeaderCell = (column) => {
    const key = metricSortKey(column.id);
    return (
      <TableCell
        align="right"
        sortDirection={sort.key === key ? sort.direction : false}
        sx={{ ...HEADER_CELL_SX, minWidth: 120 }}
      >
        <TableSortLabel
          active={sort.key === key}
          direction={sort.key === key ? sort.direction : 'asc'}
          onClick={() => handleSort(key)}
          sx={headerSortLabelSx('right')}
        >
          <Box component="span" sx={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.25 }}>
            <Box component="span" sx={{ lineHeight: 1.25 }}>
              {column.composite === 'qualityAverage' ? (
                <Tooltip title={qualityMetricNote(indicators)} describeChild>
                  <Box component="span">{column.label}</Box>
                </Tooltip>
              ) : (
                column.label
              )}
            </Box>
            {column.note && (
              <Chip
                size="small"
                label={column.note}
                sx={{ height: 18, fontSize: '0.65rem' }}
              />
            )}
          </Box>
        </TableSortLabel>
      </TableCell>
    );
  };

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1.5}
        alignItems={{ sm: 'center' }}
        sx={{ mb: 2 }}
      >
        <TextField
          size="small"
          label={`Search ${level === 'district' ? 'districts' : 'regions'}`}
          value={search}
          onChange={(event) => onSearch(event.target.value)}
          sx={{ minWidth: 240, flex: 1 }}
        />
        <FormControlLabel
          control={
            <Switch
              checked={showAllMetrics}
              onChange={(event) => {
                const next = event.target.checked;
                setSort((current) => {
                  if (!isMetricSortKey(current.key)) return current;
                  const nextColumns = next ? allMetricsColumns : metricColumns;
                  const id = metricIdFromSortKey(current.key);
                  return nextColumns.some((column) => column.id === id)
                    ? current
                    : { key: 'rank', direction: 'asc' };
                });
                onToggleAllMetrics(next);
              }}
            />
          }
          label="Show all metrics"
        />
        <CSVLink
          data={csvData}
          headers={csvHeaders}
          filename={`${csvPrefix}-${level}-opportunity-scores.csv`}
          style={{ textDecoration: 'none' }}
        >
          <Button size="small" variant="outlined" startIcon={<DownloadOutlinedIcon />}>
            Export CSV
          </Button>
        </CSVLink>
      </Stack>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {visibleRows.length} of {rows.length} shown. Click a row for the contribution breakdown.
      </Typography>

      <TableContainer sx={{ maxHeight: 640, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              {headerCell('rank', 'Rank')}
              {headerCell('name', nameColumnLabel({ level, levelConfig, showAllMetrics }))}
              {headerCell('total', 'Opportunity Score', 'right')}
              {headerCell(
                'enrollment',
                showAllMetrics
                  ? `${levelConfig?.enrollmentYear || '2025-2026'} Enrollment`
                  : enrollmentColumnLabel(levelConfig?.enrollmentField, levelConfig?.enrollmentYear),
                'right'
              )}
              {extraColumns.map((column) => metricHeaderCell(column))}
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleRows.map((row) => {
              const selected = selectedName === row.entity.name;
              return (
                <TableRow
                  key={row.entity.name}
                  hover
                  selected={selected}
                  onClick={() => onSelect(row)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell>{row.rank}</TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {row.entity.name.replace(/ School District$/, '').replace(/ Planning Region$/, '')}
                    </Typography>
                    <Stack direction="row" spacing={0.75} sx={{ mt: 0.25, flexWrap: 'wrap', gap: 0.5 }}>
                      {level === 'district' && metaField && row.entity[metaField] && (
                        <Typography variant="caption" color="text.secondary">
                          {String(row.entity[metaField]).replace(/ Planning Region$/, '')}
                        </Typography>
                      )}
                      {level === 'region' && metaField && (
                        <Typography variant="caption" color="text.secondary">
                          {row.entity[metaField]} districts
                        </Typography>
                      )}
                      {hasAlliance && row.entity.allianceDistrict && (
                        <Chip size="small" label="Alliance" color="primary" variant="outlined" />
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell align="right" sx={{ minWidth: 116 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
                      {formatScore(row.total)}
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      value={Math.max(0, Math.min(100, (toPoints(row.total) / TOTAL_SCORE_MAX) * 100))}
                      aria-label={`${row.entity.name} opportunity score out of ${TOTAL_SCORE_MAX}`}
                      sx={{
                        mt: 0.4,
                        height: 4,
                        borderRadius: 3,
                        bgcolor: 'grey.100',
                        '& .MuiLinearProgress-bar': { bgcolor: 'primary.main' },
                      }}
                    />
                  </TableCell>
                  <TableCell align="right" sx={NUMERIC_CELL_SX}>
                    {formatValue(row.entity.k12Enrollment2025_2026, 'integer')}
                  </TableCell>
                  {extraColumns.map((column) => (
                    <TableCell key={column.id} align="right" sx={NUMERIC_CELL_SX}>
                      {metricDisplayValue(row, column, indicators)}
                    </TableCell>
                  ))}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default RankingTable;
